import { FormEvent, useState } from "react";
import {
  useQuery,
  getBuildingDocuments,
  createBuildingDocument,
  deleteBuildingDocument,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { FileText, Plus, Trash2, ExternalLink } from "lucide-react";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../../client/hooks/useRequireSectionVisible";
import {
  DOCUMENT_CATEGORY_LABELS,
} from "../modules";

export default function BuildingDocumentsPage() {
  useRequireSectionVisible("showBuildingDocument");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id || "";
  const canManage = !!user?.isAdmin;

  const { data: docs, isLoading, refetch } = useQuery(
    getBuildingDocuments,
    { locationId },
    { enabled: !!locationId },
  );

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("EXIT_PLAN");
  const [fileUrl, setFileUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setBusy(true);
    setError("");
    try {
      await createBuildingDocument({
        locationId,
        title,
        description,
        category,
        fileUrl: fileUrl || undefined,
      });
      setTitle("");
      setDescription("");
      setFileUrl("");
      setShowForm(false);
      await refetch();
    } catch (err: any) {
      setError(err?.message || "Could not create document");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
              Building management
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight">Documents</h1>
            <p className="mt-2 text-neutral-600">
              Official building files — exit plans, blueprints, rules, and
              certificates. Available to any portal pack that enables this
              module.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-semibold text-white"
            >
              <Plus className="h-4 w-4" />
              Add document
            </button>
          )}
        </div>

        {!locationId && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Select a location to view building documents.
          </p>
        )}

        {showForm && (
          <form
            onSubmit={onCreate}
            className="mb-6 space-y-3 rounded-2xl border border-neutral-200 bg-white p-5"
          >
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Title</span>
              <input
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                placeholder="Tower A evacuation map"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Category</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              >
                {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                rows={3}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">File / plan URL</span>
              <input
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                placeholder="https://…"
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : "Publish document"}
            </button>
          </form>
        )}

        {isLoading && <p className="text-neutral-500">Loading documents…</p>}

        <div className="space-y-3">
          {(docs || []).map((doc: any) => (
            <article
              key={doc.id}
              className="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-4"
            >
              <div className="flex gap-3">
                <div className="rounded-xl bg-sky-50 p-2 text-sky-700">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold text-neutral-900">{doc.title}</h2>
                  <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    {DOCUMENT_CATEGORY_LABELS[doc.category] || doc.category}
                  </p>
                  {doc.description && (
                    <p className="mt-1 text-sm text-neutral-600">
                      {doc.description}
                    </p>
                  )}
                  {(doc.contentBody || doc.images?.[0]) && (
                    <a
                      href={doc.contentBody || doc.images[0]}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:underline"
                    >
                      Open file <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={async () => {
                    await deleteBuildingDocument({ id: doc.id });
                    await refetch();
                  }}
                  className="rounded-lg p-2 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </article>
          ))}
          {locationId && !isLoading && (docs || []).length === 0 && (
            <p className="rounded-2xl border border-dashed border-neutral-200 px-4 py-10 text-center text-neutral-500">
              No building documents yet.
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
